// 精算・連携（design/README.md §8）。連携カード4枚＋口座管理・支払予定テーブル＋照合用CSV。
// 支払予定は affiliate-harness の GET /api/payments?month=（gifting 有償＋アフィリ報酬を統合済み）を person で束ね、
// gifting の payee（振込先・インボイス）と gifting_influencer_id で突合する。
import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { AffPaymentItem, GfPayee, GfTabProps, Loadable } from "../../../types/gifting";
import { fetchGfPayees } from "../../../api/giftingClient";
import { buildPayoutCsv, fmtDateTime, fmtYen, igDetectCardOf, type PayoutCsvRow } from "../../../lib/giftingRules";
import { useGfIgDetectStatus } from "../useGfIgDetect";
import { useGfShipStatus } from "../useGfShipStatus";
import { GfButton, GfCard, GfEmpty, GfErrorBanner, GfSkeleton, GfTag, downloadText, type GfTone } from "../ui/GfUi";
import { errMsg } from "./kit/helpers";

interface PayRow {
  key: string;
  influencerId: string | null;
  name: string;
  fixed: number;
  affiliate: number;
  payee: GfPayee | null;
}

const ACCOUNT_TYPE_JA = { ordinary: "普通", checking: "当座" } as const;

function invoiceOf(p: GfPayee | null): { text: string; tone: GfTone | "plain" } {
  if (!p || p.invoice_status === "unsubmitted") return { text: "未提出", tone: "caution" };
  if (p.invoice_status === "exempt") return { text: "免税事業者", tone: "plain" };
  return { text: p.invoice_number || "登録事業者（番号未入力）", tone: "plain" };
}

export function SettleTab({ data, nav, month, refresh, toast }: GfTabProps) {
  const { affiliates, monthly, payments } = data;
  const { status: shipStatus } = useGfShipStatus(data.board);
  const { status: igStatus, running: igRunning, run: runIgDetect } = useGfIgDetectStatus(data.board);
  const [payees, setPayees] = useState<Loadable<GfPayee[]>>({ status: "loading" });
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let live = true;
    fetchGfPayees()
      .then((d) => live && setPayees({ status: "ok", data: d }))
      .catch((e: unknown) => live && setPayees({ status: "error", message: errMsg(e) }));
    return () => {
      live = false;
    };
  }, [reload, data.board]);

  const payeeById = useMemo(() => new Map((payees.status === "ok" ? payees.data : []).map((p) => [p.influencer_id, p])), [payees]);

  // 未払いの項目を person で束ねる（同じ gifting_influencer_id は1行に）
  const rows: PayRow[] = useMemo(() => {
    if (payments.status !== "ok") return [];
    const map = new Map<string, PayRow>();
    const add = (it: AffPaymentItem) => {
      const key = it.gifting_influencer_id ?? `name:${it.person_name}`;
      const cur =
        map.get(key) ??
        ({
          key,
          influencerId: it.gifting_influencer_id,
          name: it.person_name,
          fixed: 0,
          affiliate: 0,
          payee: it.gifting_influencer_id ? payeeById.get(it.gifting_influencer_id) ?? null : null,
        } satisfies PayRow);
      if (it.source === "gifting") cur.fixed += it.amount;
      else cur.affiliate += it.amount;
      map.set(key, cur);
    };
    payments.data.items.filter((it) => it.payment_status !== "paid" && it.amount > 0).forEach(add);
    return [...map.values()].sort((a, b) => b.fixed + b.affiliate - (a.fixed + a.affiliate));
  }, [payments, payeeById]);

  const total = rows.reduce((s, r) => s + r.fixed + r.affiliate, 0);
  const monthNum = Number(month.slice(5));

  const exportCsv = () => {
    const csvRows: PayoutCsvRow[] = rows.map((r) => {
      const p = r.payee;
      return {
        name: r.name,
        holderKana: p?.account_holder_kana ?? "",
        bank: p?.bank_name ?? "",
        branch: p?.branch_name ?? "",
        accountType: p?.account_type ? ACCOUNT_TYPE_JA[p.account_type] : "",
        last4: p?.account_last4 ?? "",
        invoice: !p ? "未提出" : p.invoice_status === "registered" ? p.invoice_number ?? "" : p.invoice_status === "exempt" ? "免税事業者" : "未提出",
        fixed: r.fixed,
        affiliate: r.affiliate,
      };
    });
    downloadText(`stoq-gifting-payouts-${month}.csv`, buildPayoutCsv(csvRows, month));
  };

  // ---- 連携カード ----
  const shopify: { tone: GfTone; label: string; detail: string } =
    affiliates.status === "ok"
      ? {
          tone: "ok",
          label: "接続済",
          detail: monthly.status === "ok" ? `紹介コード・クーポン経由 今月 ${monthly.data.shopify.conversions}件` : "アフィリ計測を取得できています",
        }
      : affiliates.status === "loading"
        ? { tone: "muted", label: "確認中", detail: "接続状態を確認しています" }
        : { tone: "warn", label: "取得失敗", detail: `アフィリ基盤に接続できませんでした（${affiliates.message}）` };

  const ss = shipStatus.status === "ok" ? shipStatus.data : null;
  const shipCard: { tone: GfTone; label: string; detail: string } =
    shipStatus.status === "loading"
      ? { tone: "muted", label: "確認中", detail: "接続状態を確認しています" }
      : shipStatus.status === "error"
        ? { tone: "warn", label: "取得失敗", detail: `出荷連携の状態を取得できませんでした（${shipStatus.message}）` }
        : ss!.last_error
          ? { tone: "warn", label: "同期エラー", detail: `直近のエラー: ${ss!.last_error}` }
          : {
              tone: "ok",
              label: "接続済み",
              detail: `最終同期 ${ss!.last_sync_at ? fmtDateTime(ss!.last_sync_at) : "未実行"}・追跡待ち ${ss!.pending}件${ss!.failed > 0 ? `・送信失敗 ${ss!.failed}件` : ""}`,
            };

  const igs = igStatus.status === "ok" ? igStatus.data : null;
  const igCard: { tone: GfTone; label: string; detail: string } =
    igStatus.status === "loading"
      ? { tone: "muted", label: "確認中", detail: "接続状態を確認しています" }
      : igStatus.status === "error"
        ? { tone: "warn", label: "取得失敗", detail: `投稿検知の状態を取得できませんでした（${igStatus.message}）` }
        : igDetectCardOf(igs!);

  const checkIg = async () => {
    try {
      const r = await runIgDetect();
      await refresh();
      toast(
        r.errors.length > 0
          ? `確認しました（新しい投稿 ${r.new}件・エラー ${r.errors.length}件）`
          : r.new > 0
            ? `新しい投稿を ${r.new}件見つけました（案件に自動記録 ${r.linked}件）`
            : "新しいタグ付け投稿はありませんでした"
      );
    } catch (e) {
      toast(`確認できませんでした: ${errMsg(e)}`);
    }
  };

  const cards: Array<{ name: string; role: string; tone: GfTone; label: string; detail: string; action?: ReactNode }> = [
    { name: "Shopify", role: "販売媒体・アフィリ計測", ...shopify },
    { name: "楽天市場", role: "販売媒体・クリック計測のみ", tone: "muted", label: "未接続", detail: "売上計測は未接続です" },
    { name: "Amazon", role: "販売媒体・クリック計測のみ", tone: "muted", label: "未接続", detail: "売上計測は未接続です" },
    { name: "倉庫出荷（stoq-worker）", role: "0円出荷指示・追跡番号", ...shipCard },
    {
      name: "Instagram 投稿検知",
      role: "写真に @stoq_kitchen をタグ付けした投稿を毎時自動で記録",
      ...igCard,
      action: (
        <>
          {igs && igs.counts.creator_no_deal + igs.counts.unknown > 0 && (
            <button type="button" className="gf-link-btn gf-conn-link" onClick={() => nav.set({ tab: "assets" })}>
              未紐づけ {igs.counts.creator_no_deal + igs.counts.unknown}件を素材庫で確認
            </button>
          )}
          <GfButton size="sm" disabled={igRunning || igStatus.status === "loading"} onClick={() => void checkIg()}>
            {igRunning ? "確認中…" : "今すぐ確認"}
          </GfButton>
        </>
      ),
    },
  ];

  return (
    <>
      <div className="gf-page-head">
        <h1 className="gf-h1">精算・連携</h1>
      </div>

      <div className="gf-conn-grid">
        {cards.map((c) => (
          <GfCard key={c.name} className="gf-conn-card">
            <div className="gf-conn-head">
              <span className="gf-conn-name">{c.name}</span>
              <GfTag tone={c.tone}>{c.label}</GfTag>
            </div>
            <span className="gf-note">{c.role}</span>
            <span className="gf-ink2 gf-small">{c.detail}</span>
            {c.action && <div className="gf-conn-actions">{c.action}</div>}
          </GfCard>
        ))}
      </div>

      <GfCard flush>
        <div className="gf-card-pad-head">
          <h2 className="gf-h2">
            口座管理・支払予定（{monthNum}月分）
          </h2>
          <div className="gf-export">
            <span className="gf-note">照合用CSV（全銀形式ではありません）</span>
            <GfButton size="sm" disabled={rows.length === 0} onClick={exportCsv}>
              振込データ出力
            </GfButton>
          </div>
        </div>

        {payments.status === "error" && (
          <div className="gf-pad-x gf-pad-b">
            <GfErrorBanner message={`支払予定を取得できませんでした（${payments.message}）`} />
          </div>
        )}
        {payees.status === "error" && (
          <div className="gf-pad-x gf-pad-b">
            <GfErrorBanner message={`振込先情報を取得できませんでした（${payees.message}）`} onRetry={() => setReload((r) => r + 1)} />
          </div>
        )}
        {payments.status === "loading" && (
          <div className="gf-pad-x gf-pad-b">
            <GfSkeleton height={40} />
          </div>
        )}
        {payments.status === "ok" && (
          <div className="gf-table-wrap">
            <table className="gf-table gf-pay-table">
              <thead>
                <tr>
                  <th>クリエイター</th>
                  <th>振込先</th>
                  <th>インボイス</th>
                  <th>内訳</th>
                  <th className="is-right">支払額</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const p = r.payee;
                  const inv = invoiceOf(p);
                  return (
                    <tr key={r.key}>
                      <td>
                        {r.influencerId ? (
                          <button type="button" className="gf-link-btn gf-name-link" onClick={() => nav.openCreator(r.influencerId as string)}>
                            {r.name}
                          </button>
                        ) : (
                          <span className="gf-name-strong">{r.name}</span>
                        )}
                      </td>
                      <td className="gf-small">
                        {!r.influencerId ? (
                          <GfTag tone="muted">ギフティング未連携</GfTag>
                        ) : p && p.bank_name ? (
                          <div className="gf-name-cell-text">
                            <span>{`${p.bank_name}${p.branch_name ? ` ${p.branch_name}` : ""}`}</span>
                            <span className="gf-mono gf-muted">
                              {p.account_last4 ? `${p.account_type ? ACCOUNT_TYPE_JA[p.account_type] : ""} ****${p.account_last4}` : "—"}
                            </span>
                          </div>
                        ) : payees.status === "loading" ? (
                          <span className="gf-faint">…</span>
                        ) : (
                          <GfTag tone="warn">口座未登録</GfTag>
                        )}
                      </td>
                      <td className="gf-small">
                        {!r.influencerId ? <span className="gf-faint">—</span> : <span className={inv.tone === "caution" ? "gf-caution-text" : undefined}>{inv.text}</span>}
                      </td>
                      <td className="gf-small gf-ink2">
                        {[r.fixed > 0 ? `固定${fmtYen(r.fixed)}` : "", r.affiliate > 0 ? `アフィリ${fmtYen(r.affiliate)}` : ""].filter(Boolean).join(" ＋ ")}
                      </td>
                      <td className="is-num">{fmtYen(r.fixed + r.affiliate)}</td>
                    </tr>
                  );
                })}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={4} className="gf-pay-foot">
                      合計（{rows.length}名）
                    </td>
                    <td className="is-num gf-strong">{fmtYen(total)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
            {rows.length === 0 && <GfEmpty>{monthNum}月分の未払いの支払予定はありません</GfEmpty>}
          </div>
        )}
        {payments.status === "ok" && <div className="gf-pad-x gf-pad-b gf-note">支払済みの項目は含みません。口座番号は下4桁のみ管理しています。</div>}
      </GfCard>
    </>
  );
}
